import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Grid,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, AccountBalance, Refresh, ChevronLeft, ChevronRight } from '@mui/icons-material';
import api from '../../services/api';

const defaultForm = {
  account_name: '',
  account_number: '',
  bank_name: '',
  is_active: false
};

const PaymentDetails = () => {
  const { t } = useTranslation(['paymentMethods', 'common']);

  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Pagination state (matching PaymentMethod page)
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Custom rows per page state
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');

  // Fetch payment details
  const fetchPaymentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/wallet/payment-details/', {
        params: {
          page: page + 1, // 1-based for backend
          page_size: rowsPerPage
        }
      });

      if (response.data) {
        // Handle both paginated response (with results/count) and direct array response
        if (Array.isArray(response.data)) {
          setPaymentDetails(response.data);
          setTotalCount(response.data.length);
          setTotalPages(Math.ceil(response.data.length / rowsPerPage));
        } else if (response.data.results) {
          setPaymentDetails(response.data.results);
          setTotalCount(response.data.count);
          setTotalPages(Math.ceil(response.data.count / rowsPerPage));
        } else {
          setPaymentDetails([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        setPaymentDetails([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch payment details',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  // Add serial numbers to rows (matching PaymentMethod page)
  const addSerialNumbers = (data, currentPage, rowsPerPageValue) => {
    return data.map((item, index) => ({
      ...item,
      serialNumber: currentPage * rowsPerPageValue + index + 1
    }));
  };

  // Pagination handlers (matching PaymentMethod page)
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
    setCustomRowsInput('');
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (editId) {
        // Update existing payment detail
        await api.put(`/admin/wallet/payment-details/${editId}/`, form);
        setSnackbar({
          open: true,
          message: 'Payment details updated successfully',
          severity: 'success'
        });
      } else {
        // Create new payment detail
        await api.post('/admin/wallet/payment-details/', form);
        setSnackbar({
          open: true,
          message: 'Payment details created successfully',
          severity: 'success'
        });
      }

      handleClose();
      fetchPaymentDetails();
    } catch (error) {
      console.error('Error saving payment details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save payment details',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment detail?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/admin/wallet/payment-details/${id}/`);
      setSnackbar({
        open: true,
        message: 'Payment details deleted successfully',
        severity: 'success'
      });
      fetchPaymentDetails();
    } catch (error) {
      console.error('Error deleting payment details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete payment details',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal open/close
  const handleOpen = (paymentDetail = null) => {
    if (paymentDetail) {
      setEditId(paymentDetail.id);
      setForm({
        account_name: paymentDetail.account_name || '',
        account_number: paymentDetail.account_number || '',
        bank_name: paymentDetail.bank_name || '',
        is_active: paymentDetail.is_active || false
      });
    } else {
      setEditId(null);
      setForm(defaultForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm(defaultForm);
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // DataGrid columns
  const columns = [
    {
      field: 'serialNumber',
      headerName: t('table.sn'),
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'account_name',
      headerName: t('paymentDetailsTable.accountName'),
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'account_number',
      headerName: t('paymentDetailsTable.accountNumber'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'bank_name',
      headerName: t('paymentDetailsTable.bankName'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'operator',
      headerName: t('paymentDetailsTable.operator'),
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'operator_ip',
      headerName: t('paymentDetailsTable.operatorIp'),
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'last_updated_by',
      headerName: t('paymentDetailsTable.lastUpdatedBy'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: t('paymentDetailsTable.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('paymentDetailsTable.active') : t('paymentDetailsTable.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'updated_at',
      headerName: t('paymentDetailsTable.updatedDate'),
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value ? new Date(params.value).toLocaleString() : '-'}
        </Typography>
      )
    },
    {
      field: 'created_at',
      headerName: t('paymentDetailsTable.createdDate'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('paymentDetailsTable.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('tooltips.edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tooltips.delete')}>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {t('paymentDetails.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPaymentDetails}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            {t('paymentDetails.addPaymentDetails')}
          </Button>
        </Box>
      </Box>

      {/* Stats Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {paymentDetails.filter(pd => pd.is_active).length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('paymentDetails.activeAccounts')}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {totalCount}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('paymentDetails.totalAccounts')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            {!paymentDetails.length && !loading ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <AccountBalance sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  {t('paymentDetails.noPaymentDetailsFound')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('paymentDetails.clickAddPaymentDetails')}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(paymentDetails, page, rowsPerPage)}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance />
            {editId ? t('paymentDetails.editPaymentDetails') : t('paymentDetails.addPaymentDetails')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t('paymentDetails.accountName')}
              value={form.account_name}
              onChange={(e) => handleFormChange('account_name', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label={t('paymentDetails.accountNumber')}
              value={form.account_number}
              onChange={(e) => handleFormChange('account_number', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label={t('paymentDetails.bankName')}
              value={form.bank_name}
              onChange={(e) => handleFormChange('bank_name', e.target.value)}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{t('paymentDetails.activeAccount')}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('paymentDetails.onlyOneAccountCanBeActiveAtATime')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('paymentDetails.cancelPaymentDetails')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !form.account_name || !form.account_number || !form.bank_name}
          >
              {loading ? <CircularProgress size={20} /> : (editId ? t('paymentDetails.updatePaymentDetails') : t('paymentDetails.addPaymentDetails'))}
            </Button>
{/* 
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !form.account_name || !form.account_number || !form.bank_name}
          >
              {loading ? <CircularProgress size={20} /> : (editId ? t('paymentDetails.updatePaymentDetails') : t('paymentDetails.addPaymentDetails'))}
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentDetails;

